import { isInStock } from "@babackapp/return-widget-components/lib/variant-availability.lib";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { useEffect, useMemo, useRef, useState } from "react";

type VariantOption = { name: string; value: string };

type Variant = {
  id: string;
  selectedOptions: VariantOption[];
  inventory_quantity: number;
  inventoryPolicy?: string;
  image?: string;
};

type Product = {
  productId: string;
  productTitle: string;
  image?: string;
  variants: Variant[];
  options: { name: string; values: string[]; position: number }[];
};

function findVariant(
  variants: Variant[],
  selectedOptions: Record<string, string>,
): Variant | undefined {
  return variants.find((v) =>
    v.selectedOptions.every((o) => selectedOptions[o.name] === o.value),
  );
}

export function getUniqueValues(
  variants: Variant[],
  optName: string,
): string[] {
  const seen = new Set<string>();
  const values: string[] = [];
  for (const v of variants) {
    const opt = v.selectedOptions.find((o) => o.name === optName);
    if (opt && !seen.has(opt.value)) {
      seen.add(opt.value);
      values.push(opt.value);
    }
  }
  return values;
}

function optionValue(v: Variant, name: string): string | undefined {
  return v.selectedOptions.find((o) => o.name === name)?.value;
}

function optionsOf(v: Variant | undefined): Record<string, string> {
  const opts: Record<string, string> = {};
  for (const o of v?.selectedOptions ?? []) opts[o.name] = o.value;
  return opts;
}

function firstAvailableVariant(
  product: Product | undefined,
  minimalStock: number,
): Variant | undefined {
  return product?.variants.find((v) => isInStock(v, minimalStock));
}

function isPurchase(p: Product, purchasedProductId: string | undefined) {
  return p.productId === purchasedProductId;
}

/** Options with a real value, in Shopify `position` order (the cascade order). */
function realOptionNames(product: Product | undefined): string[] {
  return [...(product?.options ?? [])]
    .sort((a, b) => a.position - b.position)
    .map((opt) => opt.name)
    .filter((name) =>
      getUniqueValues(product?.variants ?? [], name).some(
        (v) => v !== "Default Title",
      ),
    );
}

/**
 * The in-stock variant reached by picking `value` for `name`. Options are
 * cascading: the options before `name` and the new value are kept, so a later
 * option never locks an earlier one (Red-S + Blue-M stays reachable from
 * Red-S). Among those variants, the one keeping the earliest later choices
 * wins, so switching Red-M-Cotton to Blue keeps M when Blue-M-Linen is in stock.
 */
function resolveOptionChange(
  product: Product,
  optionNames: string[],
  selectedOptions: Record<string, string>,
  name: string,
  value: string,
  minimalStock: number,
): Variant | undefined {
  const next = { ...selectedOptions, [name]: value };
  const index = optionNames.indexOf(name);
  const fixed = optionNames.slice(0, index + 1);
  const later = optionNames.slice(index + 1);
  let best: Variant | undefined;
  let bestScore = -1;
  for (const v of product.variants) {
    if (!isInStock(v, minimalStock)) continue;
    if (!fixed.every((n) => optionValue(v, n) === next[n])) continue;
    // Weighted by position: keeping the size beats keeping the material.
    const score = later.reduce(
      (sum, n, i) =>
        optionValue(v, n) === next[n] ? sum + 2 ** (later.length - i) : sum,
      0,
    );
    if (score > bestScore) {
      best = v;
      bestScore = score;
    }
  }
  return best;
}

export type OptionChoice = {
  value: string;
  available: boolean;
  /** Image of the variant this choice leads to, for the hover preview. */
  imageUrl: string | undefined;
};

type ProductOption = {
  choices: (OptionChoice & { label: string })[];
  locked: boolean;
};

type SelectableOption = {
  name: string;
  choices: OptionChoice[];
  /**
   * Nothing else to pick: the current value is the only reachable one, or no
   * value is. A current value gone out of stock keeps the select open.
   */
  locked: boolean;
};

function isLocked(
  choices: OptionChoice[],
  current: string | undefined,
): boolean {
  const available = choices.filter((c) => c.available);
  return (
    available.length === 0 ||
    (available.length === 1 && available[0].value === current)
  );
}

function buildSelectableOptions(
  product: Product | undefined,
  selectedOptions: Record<string, string>,
  minimalStock: number,
): SelectableOption[] {
  // Nothing in stock: no option can be picked, so no empty select is shown.
  if (!product || !firstAvailableVariant(product, minimalStock)) return [];
  const optionNames = realOptionNames(product);
  return optionNames.map((name) => {
    const choices = getUniqueValues(product.variants, name)
      .filter((v) => v !== "Default Title")
      .map((value) => {
        const target = resolveOptionChange(
          product,
          optionNames,
          selectedOptions,
          name,
          value,
          minimalStock,
        );
        return {
          value,
          available: Boolean(target),
          imageUrl: target?.image || product.image,
        };
      });
    return { name, choices, locked: isLocked(choices, selectedOptions[name]) };
  });
}

type UseVariantSelectorResult = {
  /**
   * Product select: null when the purchased product is the only one on
   * offer, so a single-variant purchase still renders nothing.
   */
  productOption: ProductOption | null;
  selectedProductId: string;
  selectedOptions: Record<string, string>;
  selectedProduct: Product | undefined;
  selectedImageUrl: string | undefined;
  selectableOptions: SelectableOption[];
  hasRealChoice: boolean;
  handleProductChange: (productId: string) => void;
  handleOptionChange: (name: string, value: string) => void;
};

type Selection = { productId: string; options: Record<string, string> };

/**
 * Where the picker starts: the draft variant if still in stock, so going back
 * to this step keeps the customer's choice, else the purchased product (the
 * first listed one when absent) on its first in-stock variant.
 */
function initialSelectionFor(
  products: Product[],
  purchasedProductId: string | undefined,
  initialVariantId: string | null,
  minimalStock: number,
): Selection {
  if (initialVariantId) {
    for (const product of products) {
      const variant = product.variants.find(
        (v) => v.id === initialVariantId && isInStock(v, minimalStock),
      );
      if (variant) {
        return { productId: product.productId, options: optionsOf(variant) };
      }
    }
  }
  const product =
    products.find((p) => isPurchase(p, purchasedProductId)) ?? products[0];
  return {
    productId: product?.productId ?? "",
    options: optionsOf(firstAvailableVariant(product, minimalStock)),
  };
}

export function useVariantSelector(
  orderId: string,
  lineItemId: string,
  shopId: string,
  onVariantChange: (variantId: string | null) => void,
  /** Variant already in the draft: going back to this step must keep it. */
  initialVariantId: string | null = null,
): UseVariantSelectorResult {
  const { data: order } = usePublicOrder(orderId);
  const { data: shop } = usePublicShop(shopId);

  type ShopWithExchange = { exchange?: { minimalStock?: number } };
  type OrderWithVariants = {
    variantByLineItemId?: Record<string, { products: Product[] }>;
  };
  const minimalStock: number =
    (shop as ShopWithExchange)?.exchange?.minimalStock ?? 0;
  const typedOrder = order as OrderWithVariants | undefined;
  const allProducts = typedOrder?.variantByLineItemId?.[lineItemId]?.products;
  const purchasedLineItem = order?.lineItems?.find(
    (li) => li.line_item_id === lineItemId,
  );
  const purchasedProductId = purchasedLineItem?.product?.productId;

  // A sibling with nothing in stock has nothing to offer and is hidden. The
  // purchased product always stays, so a sold-out purchase is never swapped
  // for a sibling the customer did not pick. In collection mode the back
  // leaves out a product with no eligible variant, purchase included: it is
  // rebuilt here from the line item, with no variant, so it shows disabled and
  // starts the picker with nothing selected. Done client-side only: the legacy
  // widget crashes on a product without variants (`ExchangeResolution`).
  // A product in two exchange collections comes twice from the back: kept once.
  const purchasedTitle = purchasedLineItem?.title;
  const purchasedImage = purchasedLineItem?.image;
  const products = useMemo(() => {
    const seen = new Set<string>();
    const list = (allProducts ?? []).filter((p) => {
      if (seen.has(p.productId)) return false;
      seen.add(p.productId);
      return (
        isPurchase(p, purchasedProductId) ||
        Boolean(firstAvailableVariant(p, minimalStock))
      );
    });
    if (
      list.length === 0 ||
      !purchasedProductId ||
      list.some((p) => isPurchase(p, purchasedProductId))
    ) {
      return list;
    }
    return [
      {
        productId: purchasedProductId,
        productTitle: purchasedTitle ?? "",
        image: purchasedImage,
        variants: [],
        options: [],
      },
      ...list,
    ];
  }, [
    allProducts,
    purchasedProductId,
    purchasedTitle,
    purchasedImage,
    minimalStock,
  ]);

  // Scans every variant of every listed product: only run for the `useState`
  // initializer and while the stored selection is no longer listed.
  const resolveInitialSelection = () =>
    initialSelectionFor(
      products,
      purchasedProductId,
      initialVariantId,
      minimalStock,
    );
  const [storedSelection, setSelection] = useState<Selection>(
    resolveInitialSelection,
  );
  // The order never refetches, but the shop does (focus, 20 s stale): a raised
  // `minimalStock` can drop the selected sibling. Its options mean nothing on
  // another product, so the whole selection starts over, in state too, or the
  // sibling would come back by itself once the threshold drops again.
  const selectionListed = products.some(
    (p) => p.productId === storedSelection.productId,
  );
  const fallbackSelection = selectionListed
    ? undefined
    : resolveInitialSelection();
  if (
    fallbackSelection &&
    storedSelection.productId !== fallbackSelection.productId
  ) {
    setSelection(fallbackSelection);
  }
  const selection = fallbackSelection ?? storedSelection;
  const selectedProduct = products.find(
    (p) => p.productId === selection.productId,
  );
  // A product back above `minimalStock` while nothing was selected on it starts
  // on its first in-stock variant, like any product picked in the select.
  const selectedOptions = useMemo(
    () =>
      Object.keys(selection.options).length > 0
        ? selection.options
        : optionsOf(firstAvailableVariant(selectedProduct, minimalStock)),
    [selection.options, selectedProduct, minimalStock],
  );

  const selectedVariant = selectedProduct
    ? findVariant(selectedProduct.variants, selectedOptions)
    : undefined;
  const selectedImageUrl = selectedVariant?.image || selectedProduct?.image;

  const selectableOptions = useMemo(
    () =>
      buildSelectableOptions(selectedProduct, selectedOptions, minimalStock),
    [selectedProduct, selectedOptions, minimalStock],
  );

  // Shown only with an alternative product: a lone purchase renders nothing.
  const showProductSelect = products.length > 1;
  const hasRealChoice = showProductSelect || selectableOptions.length > 0;

  const productOption = useMemo((): ProductOption | null => {
    if (!showProductSelect) return null;
    const choices = products.map((p) => {
      const first = firstAvailableVariant(p, minimalStock);
      return {
        value: p.productId,
        label: p.productTitle,
        available: Boolean(first),
        imageUrl: first?.image || p.image,
      };
    });
    return { choices, locked: isLocked(choices, selection.productId) };
  }, [showProductSelect, products, minimalStock, selection.productId]);

  // The variant id to report upward, as a primitive: the sync effect below
  // depends on this value only, so it fires on a real change and never loops
  // through the parent's form.setValue.
  const targetVariantId =
    selectedVariant && isInStock(selectedVariant, minimalStock)
      ? selectedVariant.id
      : null;

  // Keep the latest callback without making its (unstable) identity a dependency.
  const onVariantChangeRef = useRef(onVariantChange);
  onVariantChangeRef.current = onVariantChange;

  useEffect(() => {
    onVariantChangeRef.current(targetVariantId);
  }, [targetVariantId]);

  function handleProductChange(productId: string) {
    const product = products.find((p) => p.productId === productId);
    if (!product) return;
    setSelection({
      productId,
      options: optionsOf(firstAvailableVariant(product, minimalStock)),
    });
  }

  function handleOptionChange(name: string, value: string) {
    if (!selectedProduct) return;
    const matched = resolveOptionChange(
      selectedProduct,
      selectableOptions.map((o) => o.name),
      selectedOptions,
      name,
      value,
      minimalStock,
    );
    setSelection({
      productId: selectedProduct.productId,
      options: matched
        ? optionsOf(matched)
        : { ...selectedOptions, [name]: value },
    });
  }

  return {
    productOption,
    selectedProductId: selection.productId,
    selectedOptions,
    selectedProduct,
    selectedImageUrl,
    selectableOptions,
    hasRealChoice,
    handleProductChange,
    handleOptionChange,
  };
}
